import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { fetchFactories, fetchMachineDashboard, fetchFactoryLines } from '../../services/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import './FactoryMachineOverview.css';

function FactoryMachineOverview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 상태 관리
  const [factoriesData, setFactoriesData] = useState([]);
  const [selectedFactoryId, setSelectedFactoryId] = useState(null);
  const [selectedLineId, setSelectedLineId] = useState(null); // 선택된 라인 ID
  const [lineList, setLineList] = useState([]); // 라인 목록
  const [machineData, setMachineData] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null); // ETL 동기화 시간
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFactoryDropdownOpen, setIsFactoryDropdownOpen] = useState(false);
  const [isLineDropdownOpen, setIsLineDropdownOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // ref로 리렌더링 방지
  const refreshIndicatorRef = useRef(null);
  const selectedFactoryIdRef = useRef(null);
  const selectedLineIdRef = useRef(null);

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      const factoryDropdown = event.target.closest('.factory-dropdown-wrapper');
      const lineDropdown = event.target.closest('.line-dropdown-wrapper');

      if (!factoryDropdown && isFactoryDropdownOpen) {
        setIsFactoryDropdownOpen(false);
      }
      if (!lineDropdown && isLineDropdownOpen) {
        setIsLineDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFactoryDropdownOpen, isLineDropdownOpen]);

  // 1. 공장 목록 조회 (최초 1회)
  useEffect(() => {
    const loadFactories = async () => {
      try {
        setLoading(true);
        const data = await fetchFactories();

        // 배열인지 확인
        const factories = Array.isArray(data) ? data : [];
        setFactoriesData(factories);

        // URL 파라미터에서 factoryId 가져오기
        const factoryIdFromUrl = searchParams.get('factoryId');

        if (factoryIdFromUrl) {
          // URL에 factoryId가 있으면 해당 공장 선택
          const factoryIdNumber = parseInt(factoryIdFromUrl, 10);
          const factoryExists = factories.some(f => f.id === factoryIdNumber);

          if (factoryExists) {
            setSelectedFactoryId(factoryIdNumber);
          } else if (factories.length > 0) {
            setSelectedFactoryId(factories[0].id);
          }
        } else {
          // URL에 factoryId가 없으면 첫 번째 공장을 기본 선택
          if (factories.length > 0) {
            setSelectedFactoryId(factories[0].id);
          }
        }
      } catch (err) {
        console.error('공장 목록 조회 실패:', err);
        setError('공장 목록을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadFactories();
  }, [searchParams]);

  // 2. 선택된 공장의 라인 목록 조회
  useEffect(() => {
    if (!selectedFactoryId) return;

    const loadLines = async () => {
      try {
        const lines = await fetchFactoryLines(selectedFactoryId);
        setLineList(lines || []);

        // URL 파라미터에서 lineId 가져오기
        const lineIdFromUrl = searchParams.get('lineId');

        if (lineIdFromUrl) {
          const lineIdNumber = parseInt(lineIdFromUrl, 10);
          setSelectedLineId(lineIdNumber);
        } else {
          setSelectedLineId(null);
        }
      } catch (err) {
        console.error('라인 목록 조회 실패:', err);
        setLineList([]);
      }
    };

    loadLines();
  }, [selectedFactoryId, searchParams]);

  // 사출기 데이터 로드 함수 (useCallback으로 메모이제이션)
  const loadMachines = useCallback(async (isAutoRefresh = false) => {
    const factoryId = selectedFactoryIdRef.current;
    const lineId = selectedLineIdRef.current;
    if (!factoryId) return;

    try {
      if (isAutoRefresh) {
        // CSS 클래스로 애니메이션 처리 (리렌더링 방지)
        if (refreshIndicatorRef.current) {
          refreshIndicatorRef.current.classList.add('refreshing');
          setTimeout(() => {
            refreshIndicatorRef.current?.classList.remove('refreshing');
          }, 1500);
        }
      } else {
        setLoading(true);
      }
      setError(null);

      const data = await fetchMachineDashboard(factoryId, lineId);
      const machines = data.machines || [];

      setMachineData(machines);
      setLastSyncTime(data.lastSyncTime);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('사출기 데이터 조회 실패:', err);
      if (!isAutoRefresh) {
        setError('데이터를 불러오는데 실패했습니다.');
      }
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  }, []);

  // 3. 선택된 라인에 따라 사출기 데이터 조회 (초기 로드)
  useEffect(() => {
    if (!selectedFactoryId) return;
    selectedFactoryIdRef.current = selectedFactoryId;
    selectedLineIdRef.current = selectedLineId;
    loadMachines(false);
  }, [selectedFactoryId, selectedLineId, loadMachines]);

  // 4. useAutoRefresh 훅 사용 (30초 쿨다운 적용)
  useAutoRefresh(() => {
    loadMachines(true);
  }, !!selectedFactoryId);

  // 현재 선택된 공장
  const currentFactory = factoriesData.find(f => f.id === selectedFactoryId) || {};

  // 현재 선택된 라인
  const currentLine = lineList.find(line => line.id === selectedLineId);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'running':
        return { text: '가동중', color: 'status-running', bgColor: 'status-bg-running', dotColor: 'status-dot-running' };
      case 'idle':
        return { text: '대기', color: 'status-idle', bgColor: 'status-bg-idle', dotColor: 'status-dot-idle' };
      case 'stopped':
        return { text: '정지', color: 'status-stopped', bgColor: 'status-bg-stopped', dotColor: 'status-dot-stopped' };
      default:
        return { text: '오류', color: 'status-stopped', bgColor: 'status-bg-stopped', dotColor: 'status-dot-stopped' };
    }
  };

  const HalfDonutChart = ({ value, size = 70 }) => {
    const radius = (size - 12) / 2;
    const circumference = Math.PI * radius;
    const strokeDasharray = `${(value / 100) * circumference} ${circumference}`;

    return (
      <div className="half-donut-container" style={{ width: size, height: size/2 + 10 }}>
        <svg width={size} height={size/2 + 10} className="half-donut-svg">
          <path
            d={`M ${6} ${size/2} A ${radius} ${radius} 0 0 1 ${size - 6} ${size/2}`}
            fill="transparent"
            stroke="#374151"
            strokeWidth="6"
          />
          <path
            d={`M ${6} ${size/2} A ${radius} ${radius} 0 0 1 ${size - 6} ${size/2}`}
            fill="transparent"
            stroke="#10B981"
            strokeWidth="6"
            strokeDasharray={strokeDasharray}
            strokeLinecap="round"
            className="half-donut-progress"
          />
        </svg>
        <div className="half-donut-label">
          <div className="half-donut-value">{Math.round(value)}%</div>
        </div>
      </div>
    );
  };

  const OperationTimeline = ({ timeline }) => {
    // API에서 받은 timeline 배열을 직접 사용
    const pattern = timeline || Array(24).fill('stopped');

    return (
      <div className="operation-timeline">
        {pattern.map((segmentStatus, i) => (
          <div
            key={i}
            className={`timeline-segment timeline-segment-${segmentStatus}`}
          />
        ))}
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="machine-overview-container">
        {/* 헤더 */}
        <div className="machine-overview-header">
          <div className="machine-overview-header-left">
            <div className="header-info-row">
              <div className="machine-overview-title">사출기별 대시보드</div>
              <p className="machine-overview-subtitle">공장내 사출기 현황</p>
            </div>
            <div className="header-divider"></div>

            {/* 공장 선택 */}
            <div className="factory-dropdown-wrapper">
              <button
                className="factory-dropdown-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFactoryDropdownOpen(!isFactoryDropdownOpen);
                }}
              >
                <span className="dropdown-factory-name">
                  {currentFactory?.factoryName || '공장을 선택하세요'}
                </span>
                <span className="material-symbols-outlined dropdown-icon">
                  {isFactoryDropdownOpen ? 'expand_less' : 'expand_more'}
                </span>
              </button>
              {isFactoryDropdownOpen && (
                <div className="factory-dropdown-menu">
                  {factoriesData.map((factory) => (
                    <div
                      key={factory.id}
                      className={`factory-dropdown-item ${selectedFactoryId === factory.id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFactoryId(factory.id);
                        setIsFactoryDropdownOpen(false);
                      }}
                    >
                      <div className="dropdown-item-name">{factory.factoryName}</div>
                      <div className="dropdown-item-location">{factory.address}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 라인 선택 */}
            <div className="line-dropdown-wrapper">
              <button
                className="line-dropdown-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLineDropdownOpen(!isLineDropdownOpen);
                }}
              >
                <span className="dropdown-line-name">
                  {currentLine?.lineName || '전체'}
                </span>
                <span className="material-symbols-outlined dropdown-icon">
                  {isLineDropdownOpen ? 'expand_less' : 'expand_more'}
                </span>
              </button>
              {isLineDropdownOpen && (
                <div className="line-dropdown-menu">
                  <div
                    className={`line-dropdown-item ${selectedLineId === null ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLineId(null);
                      setIsLineDropdownOpen(false);
                    }}
                  >
                    <div className="dropdown-item-name">전체</div>
                  </div>
                  {lineList.map((line) => (
                    <div
                      key={line.id}
                      className={`line-dropdown-item ${selectedLineId === line.id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLineId(line.id);
                        setIsLineDropdownOpen(false);
                      }}
                    >
                      <div className="dropdown-item-name">{line.lineName}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="machine-overview-header-right" ref={refreshIndicatorRef}>
            <div className="update-label-container">
              <span className="update-label">업데이트</span>
              <span className="material-symbols-outlined refresh-icon">
                sync
              </span>
            </div>
            <div className="update-time">
              {loading ? '로딩 중...' : (
                lastSyncTime
                  ? new Date(lastSyncTime).toLocaleString('ko-KR')
                  : lastUpdated.toLocaleTimeString('ko-KR')
              )}
            </div>
          </div>
        </div>

        {/* 로딩 상태 */}
        {loading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            color: 'white',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(255,255,255,0.2)',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <div>데이터를 불러오는 중...</div>
          </div>
        )}

        {/* 에러 상태 */}
        {error && !loading && (
          <div className="error-container" style={{
            padding: '20px',
            textAlign: 'center',
            color: '#ef4444'
          }}>
            <div className="error-message">{error}</div>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              새로고침
            </button>
          </div>
        )}

        {/* 빈 데이터 */}
        {!loading && !error && machineData.length === 0 && (
          <div className="empty-state" style={{
            padding: '60px',
            textAlign: 'center',
            color: '#9ca3af'
          }}>
            <p>등록된 사출기가 없습니다.</p>
          </div>
        )}

        {/* 사출기 그리드 */}
        {!loading && !error && machineData.length > 0 && (
          <div className="machines-grid">
            {machineData.map((machine) => {
            const statusInfo = getStatusInfo(machine.status);
            return (
              <div
                key={machine.id}
                className="machine-card"
                onClick={() => navigate('/machine', { state: { machineData: machine } })}
              >
                {/* 상단: 사출기 이름과 알람, 가동률 차트 */}
                <div className="machine-header">
                  <div className="machine-header-left">
                    <div className="machine-name-row">
                      <h3 className="machine-name">{machine.name}</h3>
                      <div
                        className="alarm-indicator"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/machine', {
                            state: {
                              machineData: machine,
                              openTab: 'alarm'
                            }
                          });
                        }}
                        style={{ cursor: machine.alarms > 0 ? 'pointer' : 'default' }}
                      >
                        <div className={`warning-light ${machine.alarms > 0 ? 'warning-light-active' : 'warning-light-inactive'}`}>
                          <div className="warning-light-top"></div>
                          <div className="warning-light-base"></div>
                        </div>
                        {machine.alarms > 0 && (
                          <div className="alarm-badge">{machine.alarms}</div>
                        )}
                      </div>
                    </div>

                    {/* 상태 정보 */}
                    <div className="machine-status">
                      <div className={`status-dot ${statusInfo.dotColor} ${machine.status === 'running' ? 'pulse' : ''}`}></div>
                      <span className={`status-text ${statusInfo.color}`}>{statusInfo.text}</span>
                    </div>
                  </div>

                  {/* 오른쪽: 가동률 차트 */}
                  <div className="machine-utilization">
                    <HalfDonutChart value={machine.utilization} size={70} />
                    <span className="utilization-label">24시간 가동률</span>
                  </div>
                </div>

                {/* 하단: 4개 정보 그리드 */}
                <div className="machine-info-grid">
                  <div className="info-box">
                    <div className="info-label">제품명</div>
                    <div className="info-value truncate">{machine.craftId || machine.productName || '-'}</div>
                  </div>
                  <div className="info-box">
                    <div className="info-label">샷카운트</div>
                    <div className="info-value">{machine.shotNo}</div>
                  </div>
                  <div className="info-box">
                    <div className="info-label">캐비티수</div>
                    <div className="info-value">{machine.cavityCount > 0 ? `${machine.cavityCount}개` : '-'}</div>
                  </div>
                  <div className="info-box">
                    <div className="info-label">사이클타임</div>
                    <div className="info-value">{machine.cycleTime > 0 ? `${machine.cycleTime}s` : '-'}</div>
                  </div>
                </div>

                {/* 최하단: 가동상태 타임라인 */}
                <div className="machine-timeline-section">
                  <div className="timeline-header">
                    <span className="timeline-title">24시간 가동상태</span>
                    <div className="timeline-legend-mini">
                      <div className="legend-item-mini">
                        <div className="legend-dot-mini legend-dot-green"></div>
                        <span>가동</span>
                      </div>
                      <div className="legend-item-mini">
                        <div className="legend-dot-mini legend-dot-yellow"></div>
                        <span>대기</span>
                      </div>
                      <div className="legend-item-mini">
                        <div className="legend-dot-mini legend-dot-red"></div>
                        <span>정지</span>
                      </div>
                    </div>
                  </div>
                  <OperationTimeline timeline={machine.timeline} />
                </div>
              </div>
              );
            })}
          </div>
        )}

      </div>
    </MainLayout>
  );
}

export default FactoryMachineOverview;
